import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import {
  buildCountryTitleListParams,
  normalizeCountryTitleStatus,
} from '../../../utils/country-title-catalog.utils';
import { TypeOfTaxSelectService } from './type-of-tax-select.service';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/** Payload UI / API de impuestos (taxes). */
export type TaxFormPayload = {
  /** Legacy / persistido por API. */
  taxId?: string;
  /** Select Tipo de impuesto → catálogo `tax_types`. */
  typeOfTaxId?: string;
  /** Select Tipo → catálogo `amount_types`. */
  typeAmountId?: string;
  /** Select Repetir cada → catálogo `period_span`. */
  typeOfDayId?: string | null;
  name?: string;
  title?: string;
  prefix?: string;
  code?: string;
  quantity?: string | number;
  amount?: string | number;
  exempt?: boolean;
  excento?: boolean;
  validity?: boolean;
  repetitions?: number;
  dueDate?: string | null;
  serviceIds?: string[];
  status?: string;
};

export interface TaxSelectOption {
  value: string;
  label: string;
  code?: string;
}

/**
 * Impuestos por país.
 * Endpoint: `/settings/countries/{countryId}/taxes`
 */
@Injectable({ providedIn: 'root' })
export class TaxService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly typeOfTaxSelect = inject(TypeOfTaxSelectService);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const base = `${this.apiUrl}/${countryId}/taxes`;
    return id !== undefined && id !== null ? `${base}/${id}` : base;
  }

  private mapItem(it: any): Tax {
    const serviceIds = this.mapServiceIds(it);
    const typeOfTaxId = String(it.typeOfTaxId || it.type_of_tax_id || '');
    const taxId = String(it.taxId || it.tax_id || typeOfTaxId || '');
    const quantity = it.quantity ?? it.amount ?? '0.00';
    return {
      id: it.id,
      taxId,
      typeOfTaxId: typeOfTaxId || taxId,
      typeAmountId: String(it.typeAmountId || it.type_amount_id || it.type || ''),
      typeOfDayId: it.typeOfDayId || it.type_of_day_id || null,
      name: it.name || it.title || '',
      prefix: it.code || it.prefix || '',
      quantity,
      amount: quantity,
      exempt: !!(it.exempt ?? it.excento),
      validity: !!it.validity,
      repetitions:
        it.repetitions !== undefined && it.repetitions !== null
          ? Number(it.repetitions)
          : 0,
      dueDate: it.dueDate || it.due_date || null,
      serviceIds,
      status: normalizeCountryTitleStatus(it.status),
      createdAt: it.createdAt || it.created_at || '',
      updatedAt: it.updatedAt || it.updated_at || '',
    };
  }

  private mapServiceIds(it: any): string[] {
    const raw =
      it.serviceIds ??
      it.servicesIds ??
      it.services ??
      it.service_ids ??
      [];
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item: unknown) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item);
        }
        if (item && typeof item === 'object') {
          const o = item as { id?: string | number; value?: string };
          return String(o.id ?? o.value ?? '');
        }
        return '';
      })
      .filter(Boolean);
  }

  private toApiPayload(payload: TaxFormPayload): Record<string, unknown> {
    const typeOfTaxId = String(
      payload.typeOfTaxId ?? payload.taxId ?? '',
    ).trim();
    const typeAmountId = String(payload.typeAmountId ?? '').trim();
    const exempt = !!(payload.exempt ?? payload.excento);
    const validity = !!payload.validity;
    const quantityRaw = payload.quantity ?? payload.amount ?? 0;
    const quantity =
      typeof quantityRaw === 'number'
        ? quantityRaw
        : Number(String(quantityRaw).replace(',', '.')) || 0;

    const body: Record<string, unknown> = {
      name: (payload.title ?? payload.name ?? '').trim(),
      code: (payload.code ?? payload.prefix ?? '').trim() || undefined,
      prefix: (payload.prefix ?? '').trim() || undefined,
      exempt,
      validity,
      quantity: exempt ? 0 : quantity,
      serviceIds: Array.isArray(payload.serviceIds) ? payload.serviceIds : [],
      status: String(payload.status || 'ACTIVE').toUpperCase(),
    };

    if (typeOfTaxId) {
      body['typeOfTaxId'] = typeOfTaxId;
    }

    if (!exempt && typeAmountId) {
      body['typeAmountId'] = typeAmountId;
    } else if (typeAmountId) {
      body['typeAmountId'] = typeAmountId;
    }

    if (validity) {
      const typeOfDayId = String(payload.typeOfDayId ?? '').trim();
      body['typeOfDayId'] = typeOfDayId || null;
      const repetitions = Number(payload.repetitions ?? 0);
      body['repetitions'] = Number.isFinite(repetitions)
        ? Math.max(0, repetitions)
        : 0;
      body['dueDate'] = payload.dueDate ? payload.dueDate : null;
    } else {
      body['typeOfDayId'] = null;
      body['repetitions'] = 0;
      body['dueDate'] = payload.dueDate ?? null;
    }

    return body;
  }

  /**
   * Catálogo base de impuestos (`tax_types`) vía TypeOfTaxSelectService.
   */
  getTaxTypeOptions(_countryId?: string): Observable<TaxSelectOption[]> {
    return this.typeOfTaxSelect.getOptions();
  }

  getTaxes(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<TaxPageResponse> {
    const params = buildCountryTitleListParams({
      page,
      size,
      sort,
      direction,
      search,
      status,
    });
    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          } as TaxPageResponse;
        }),
      );
  }

  getTaxById(countryId: string, id: string | number): Observable<Tax> {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createTax(countryId: string, payload: TaxFormPayload) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        this.toApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Impuesto creado',
        })),
      );
  }

  updateTax(
    countryId: string,
    id: string | number,
    payload: TaxFormPayload,
  ) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        this.toApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Impuesto actualizado',
        })),
      );
  }

  deleteTax(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Impuesto eliminado',
        })),
      );
  }
}

export interface Tax {
  id: string;
  taxId?: string;
  typeOfTaxId?: string;
  typeAmountId?: string;
  typeOfDayId?: string | null;
  name: string;
  /** UI abreviatura; API `code`. */
  prefix?: string;
  quantity?: string | number;
  /** Alias UI de quantity. */
  amount?: string | number;
  exempt?: boolean;
  validity?: boolean;
  repetitions?: number;
  dueDate?: string | null;
  serviceIds?: string[];
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaxPageResponse {
  items: Tax[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
